import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'
import SecretField from '../../../components/admin/SecretField'

/**
 * AccountEditModal - Modal form for creating/editing LLM accounts
 */
const AccountEditModal = ({ account, configId, onSubmit, onClose }) => {
  const isEditing = Boolean(account)

  const [formData, setFormData] = useState({
    name: '',
    model: '',
    api_key: '',
    is_active: true,
    max_tokens: 4096,
    input_price_per_1k: 0,
    output_price_per_1k: 0,
    priority: 1,
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Populate form when editing
  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        model: account.model || '',
        api_key: '', // Don't populate API key for security
        is_active: account.is_active ?? true,
        max_tokens: account.max_tokens || 4096,
        input_price_per_1k: account.input_price_per_1k || 0,
        output_price_per_1k: account.output_price_per_1k || 0,
        priority: account.priority || 1,
      })
    }
  }, [account])

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target
    const numValue = parseFloat(value)
    setFormData(prev => ({
      ...prev,
      [name]: isNaN(numValue) ? '' : numValue
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  // Validate form
  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.model.trim()) {
      newErrors.model = 'Model identifier is required'
    }

    // API key required only for new accounts
    if (!isEditing && !formData.api_key.trim()) {
      newErrors.api_key = 'API key is required for new accounts'
    }

    if (formData.max_tokens < 1 || formData.max_tokens > 200000) {
      newErrors.max_tokens = 'Max tokens must be between 1 and 200,000'
    }

    if (formData.input_price_per_1k < 0) {
      newErrors.input_price_per_1k = 'Price cannot be negative'
    }

    if (formData.output_price_per_1k < 0) {
      newErrors.output_price_per_1k = 'Price cannot be negative'
    }

    if (formData.priority < 1 || formData.priority > 10) {
      newErrors.priority = 'Priority must be between 1 and 10'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    try {
      setIsSubmitting(true)

      // Build submission data
      const submitData = {
        name: formData.name,
        model: formData.model,
        is_active: formData.is_active,
        max_tokens: parseInt(formData.max_tokens),
        input_price_per_1k: parseFloat(formData.input_price_per_1k),
        output_price_per_1k: parseFloat(formData.output_price_per_1k),
        priority: parseInt(formData.priority),
        config_id: configId,
      }

      // Only include API key if provided (for updates, empty means no change)
      if (formData.api_key.trim()) {
        submitData.api_key = formData.api_key
      }

      await onSubmit(submitData)
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Account' : 'Add Account'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Account Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., GPT-4o Primary"
                  className={clsx(
                    'block w-full rounded-md shadow-sm text-sm',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:ring-primary-500 focus:border-primary-500',
                    errors.name && 'border-red-500'
                  )}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Model */}
              <div>
                <label
                  htmlFor="model"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Model Identifier *
                </label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="e.g., gpt-4o, claude-sonnet-4-20250514"
                  className={clsx(
                    'block w-full rounded-md shadow-sm text-sm',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    'text-gray-900 dark:text-gray-100',
                    'focus:ring-primary-500 focus:border-primary-500',
                    errors.model && 'border-red-500'
                  )}
                />
                {errors.model && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.model}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use the exact model identifier from the provider's API documentation
                </p>
              </div>

              {/* API Key */}
              <SecretField
                id="api_key"
                label={isEditing ? 'API Key (leave empty to keep current)' : 'API Key *'}
                value={formData.api_key}
                onChange={(e) => handleChange({ target: { name: 'api_key', value: e.target.value } })}
                placeholder={isEditing ? 'Leave empty to keep current key' : 'Enter API key...'}
                error={errors.api_key}
              />

              {/* Max Tokens & Priority Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="max_tokens"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    id="max_tokens"
                    name="max_tokens"
                    value={formData.max_tokens}
                    onChange={handleNumberChange}
                    min="1"
                    max="200000"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.max_tokens && 'border-red-500'
                    )}
                  />
                  {errors.max_tokens && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.max_tokens}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="priority"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Priority
                  </label>
                  <input
                    type="number"
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleNumberChange}
                    min="1"
                    max="10"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.priority && 'border-red-500'
                    )}
                  />
                  {errors.priority && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.priority}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lower = higher priority
                  </p>
                </div>
              </div>

              {/* Pricing Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="input_price_per_1k"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Input Price ($/1K tokens)
                  </label>
                  <input
                    type="number"
                    id="input_price_per_1k"
                    name="input_price_per_1k"
                    value={formData.input_price_per_1k}
                    onChange={handleNumberChange}
                    min="0"
                    step="0.0001"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.input_price_per_1k && 'border-red-500'
                    )}
                  />
                  {errors.input_price_per_1k && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.input_price_per_1k}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="output_price_per_1k"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Output Price ($/1K tokens)
                  </label>
                  <input
                    type="number"
                    id="output_price_per_1k"
                    name="output_price_per_1k"
                    value={formData.output_price_per_1k}
                    onChange={handleNumberChange}
                    min="0"
                    step="0.0001"
                    className={clsx(
                      'block w-full rounded-md shadow-sm text-sm',
                      'border-gray-300 dark:border-gray-600',
                      'bg-white dark:bg-gray-700',
                      'text-gray-900 dark:text-gray-100',
                      'focus:ring-primary-500 focus:border-primary-500',
                      errors.output_price_per_1k && 'border-red-500'
                    )}
                  />
                  {errors.output_price_per_1k && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.output_price_per_1k}</p>
                  )}
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="is_active"
                  className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                >
                  Active (available for translations)
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'border border-gray-300 dark:border-gray-600',
                  'text-gray-700 dark:text-gray-300',
                  'bg-white dark:bg-gray-700',
                  'hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md text-white',
                  'bg-primary-600 hover:bg-primary-700',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  isEditing ? 'Update Account' : 'Create Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AccountEditModal
